import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/**
 * Provider CRM — view all clients, drill into per-client dossier, add notes
 * and tags. Pro tier only.
 */
export default function ProClients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [noteBody, setNoteBody] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/crm/clients`, { headers: headers() });
      setClients(r.data.clients || []);
    } catch (e) {
      if (e.response?.status === 403) setGated(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openClient = async (clientId) => {
    setSelected(clientId);
    setDossier(null);
    setNoteBody("");
    try {
      const r = await axios.get(`${API}/crm/clients/${clientId}`, { headers: headers() });
      setDossier(r.data);
      setTagInput((r.data.tags || []).join(", "));
    } catch (e) { toast.error("Could not load client"); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await axios.post(`${API}/crm/clients/${selected}/notes`, { body: noteBody }, { headers: headers() });
      toast.success("Note saved");
      setNoteBody("");
      openClient(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveTags = async () => {
    const tags = tagInput.split(",").map(t => t.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/crm/clients/${selected}/tags`, { tags }, { headers: headers() });
      toast.success("Tags saved");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteNote = async (nid) => {
    try {
      await axios.delete(`${API}/crm/notes/${nid}`, { headers: headers() });
      openClient(selected);
    } catch { toast.error("Failed"); }
  };

  if (gated) {
    return <Layout><div className="max-w-2xl mx-auto p-8 text-center text-amber-200/70">
      <h1 className="text-2xl font-serif text-amber-100 mb-3">Client CRM is a Pro feature</h1>
      <a href="/pricing" className="text-amber-400 underline">Upgrade to Pro →</a>
    </div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6" data-testid="pro-clients">
        <h1 className="text-3xl font-serif text-amber-100 mb-6">Clients</h1>
        {loading ? (
          <div className="text-amber-200/60">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="text-amber-200/60">No clients yet. Notes appear once someone books with you.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client list */}
            <div className="md:col-span-1 space-y-2" data-testid="client-list">
              {clients.map(c => (
                <button
                  key={c.clientId}
                  onClick={() => openClient(c.clientId)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${selected === c.clientId ? "border-amber-500/60 bg-amber-950/30" : "border-neutral-800 bg-neutral-900/40 hover:bg-neutral-900/70"}`}
                  data-testid={`client-item-${c.clientId}`}
                >
                  <div className="text-amber-100 text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-amber-200/50">{c.bookingCount} bookings · ${c.totalSpent.toFixed(2)}</div>
                  {c.tags?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1">
                      {c.tags.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/30 text-amber-200">{t}</span>)}
                    </div>
                  )}
                </button>
              ))}
            </div>
            {/* Dossier */}
            <div className="md:col-span-2">
              {!selected ? (
                <div className="text-amber-200/50 text-sm">Select a client to view their dossier</div>
              ) : !dossier ? (
                <div className="text-amber-200/60">Loading dossier…</div>
              ) : (
                <div className="space-y-6" data-testid="client-dossier">
                  <div>
                    <h2 className="text-xl font-serif text-amber-100">{dossier.client.name}</h2>
                    <div className="text-sm text-amber-200/50">{dossier.client.email}</div>
                    <div className="text-sm text-amber-200/70 mt-1">
                      {dossier.bookings.length} bookings · ${dossier.totalSpent.toFixed(2)} lifetime
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
                    <div className="text-xs text-amber-200/50 uppercase mb-2">Tags (comma-separated)</div>
                    <div className="flex gap-2">
                      <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="regular, tarot, birthday-nov" data-testid="tags-input" />
                      <Button onClick={saveTags} data-testid="save-tags">Save</Button>
                    </div>
                  </div>

                  {/* Add note */}
                  <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
                    <div className="text-xs text-amber-200/50 uppercase mb-2">Add a note (private)</div>
                    <Textarea value={noteBody} onChange={e => setNoteBody(e.target.value)} placeholder="Session insights, preferences, follow-ups…" rows={3} data-testid="note-body" />
                    <div className="flex justify-end mt-2">
                      <Button onClick={addNote} disabled={!noteBody.trim()} data-testid="save-note">Save note</Button>
                    </div>
                  </div>

                  {/* Notes list */}
                  <div>
                    <h3 className="text-amber-100 font-serif mb-3">Notes ({dossier.notes.length})</h3>
                    {dossier.notes.length === 0 && <div className="text-amber-200/50 text-sm">No notes yet.</div>}
                    <ul className="space-y-3">
                      {dossier.notes.map(n => (
                        <li key={n.id} className="p-3 rounded-lg border border-neutral-800 bg-neutral-900/40" data-testid={`note-${n.id}`}>
                          <div className="text-sm text-amber-100 whitespace-pre-wrap">{n.body}</div>
                          <div className="flex justify-between mt-2 text-xs text-amber-200/40">
                            <span>{new Date(n.createdAt).toLocaleString()}</span>
                            <button onClick={() => deleteNote(n.id)} className="hover:text-red-400" data-testid={`delete-note-${n.id}`}>Delete</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Booking history */}
                  <div>
                    <h3 className="text-amber-100 font-serif mb-3">Booking history ({dossier.bookings.length})</h3>
                    <ul className="space-y-2">
                      {dossier.bookings.slice(0, 10).map(b => (
                        <li key={b.id} className="p-3 rounded-lg border border-neutral-800 bg-neutral-900/40 flex justify-between items-center">
                          <div className="text-sm">
                            <div className="text-amber-100">{b.slotAt ? new Date(b.slotAt).toLocaleDateString() : "—"}</div>
                            <div className="text-xs text-amber-200/50">{b.status}</div>
                          </div>
                          <div className="text-amber-100 text-sm">${(b.amount || 0).toFixed(2)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
